import { Injectable } from '@angular/core';
import { Cargo } from 'src/app/core/interfaces/cargo';
import { RndcErrorSolver } from 'src/app/core/classes/rndc-error-solver';
import { Utils } from 'src/app/core/resources/utils';
import { Vehicle } from 'src/app/core/interfaces/vehicle';
import { VehiclesService } from '../../administration/vehicles/list-vehicles.service';
import { CargoTrackingService } from './cargo-tracking.service';
import { isDocumentRecreationInProgress } from 'src/app/core/resources/transport-company-change.utils';

export type RndcResolverResult =
  | { outcome: 'skipped-no-error' }
  | { outcome: 'show-error-dialog'; message: string }
  | { outcome: 'low-agreed-value-open-dialog' }
  | { outcome: 'rebuild-failed-snackbar'; consecutive: string | number }
  | { outcome: 'done-refresh'; consecutive: string | number };

@Injectable({
  providedIn: 'root'
})
export class CargoRndcSolverService {

  errorsWithUserInteraction = ['MAN220', 'MAN245', 'RTU140', 'VEH280'];

  constructor(
    private vehiclesService: VehiclesService,
    private rndcErrorSolver: RndcErrorSolver,
    private utils: Utils,
    private cargoTrackingService: CargoTrackingService
  ) { }

  async fixManifestError(cargo: Cargo): Promise<RndcResolverResult> {
    if (isDocumentRecreationInProgress(cargo)) return { outcome: 'skipped-no-error' };
    const manifestError = this.utils.getNestedValue(cargo, 'manifestError.error') as string;
    if (!manifestError) return { outcome: 'skipped-no-error' };
    if (manifestError === 'Error no controlado RNDC, probar en unos minutos')
      return this.runManifestFixWithVehicle(cargo, manifestError, (v, c) => this.rndcErrorSolver.noControlledError(c.driver, c, v));
    const errorCode = this.rndcErrorSolver.getErrorCode(manifestError).toUpperCase();
    if (!errorCode) return { outcome: 'show-error-dialog', message: manifestError };
    switch (errorCode) {
      case 'MAN220':
        return this.runManifestFixWithVehicle(cargo, manifestError, (v, c) => this.rndcErrorSolver.MAN220(v, c));
      case 'MAN245':
        return this.runManifestFixWithVehicle(cargo, manifestError, (v, c) => this.rndcErrorSolver.MAN245(c.driver, c, v));
      case 'RTU140':
        return this.runManifestFixWithVehicle(cargo, manifestError, (v, c) => this.rndcErrorSolver.RTU140(c, v));
      case 'VEH280':
        return this.runManifestFixWithVehicle(cargo, manifestError, (v, c) => this.rndcErrorSolver.VEH280(v, c));
      case 'MAN130':
        return this.runManifestFix(cargo, manifestError, () => this.cargoTrackingService.fixOwnerMinistry(cargo.id).toPromise());
      case 'MAN240':
      case 'MAN246':
      case 'MAN247':
      case 'RTU240':
        return this.runManifestFix(cargo, manifestError, () => this.cargoTrackingService.fixUserMinistry(cargo.id).toPromise());
      case 'MAN041':
      case 'MAN141':
      case 'MAN140':
        return this.runManifestFix(cargo, manifestError, () => this.cargoTrackingService.fixVehicleMinistry(cargo.id).toPromise());
      case 'MAN200':
      case 'MAN199':
        return this.runManifestFix(cargo, manifestError, () => this.cargoTrackingService.fixTrailerVehicleMinistry(cargo.id).toPromise());
      case 'MAN010':
      case 'MAN012':
        return this.runManifestFix(cargo, manifestError, () => this.cargoTrackingService.emitConsignmentRNDC(cargo.id).toPromise());
      case 'RTU287':
      case 'MAN287':
        return { outcome: 'low-agreed-value-open-dialog' };
      default:
        return { outcome: 'show-error-dialog', message: manifestError };
    }
  }

  runManifestFixWithVehicle(
    cargo: Cargo,
    manifestFallbackMessage: string,
    solver: (vehicle: Vehicle, cargo: Cargo) => Promise<boolean>
  ): Promise<RndcResolverResult> {
    return this.runVehicleSolverAndRebuild(cargo, manifestFallbackMessage, solver);
  }

  runManifestFix(
    cargo: Cargo,
    manifestFallbackMessage: string,
    solver: () => Promise<unknown>
  ): Promise<RndcResolverResult> {
    return this.runAndRebuild(cargo, manifestFallbackMessage, solver);
  }

  private async getVehicleForRndcManifestFix(licensePlate: string): Promise<Vehicle | null> {
    try {
      const vehicle = await this.vehiclesService.getVehicleFromOwnCompany(licensePlate).toPromise();
      if (!vehicle || !vehicle.id || !vehicle.driver) return null;
      return vehicle;
    } catch {
      return null;
    }
  }

  private async runVehicleSolverAndRebuild(
    cargo: Cargo,
    fallbackErrMsg: string,
    solver: (v: Vehicle, c: Cargo) => Promise<boolean>
  ): Promise<RndcResolverResult> {
    if (!cargo.licensePlate) return { outcome: 'show-error-dialog', message: fallbackErrMsg };
    const vehicle = await this.getVehicleForRndcManifestFix(cargo.licensePlate);
    if (!vehicle) return { outcome: 'show-error-dialog', message: fallbackErrMsg };
    try {
      await solver(vehicle, cargo);
    } catch (e) {
      console.error(e);
      return { outcome: 'show-error-dialog', message: fallbackErrMsg };
    }
    const rebuilt = await this.rebuild(cargo);
    if (rebuilt.outcome !== 'done-refresh') return rebuilt;
    return { outcome: 'done-refresh', consecutive: rebuilt.consecutive };
  }

  private async runAndRebuild(
    cargo: Cargo,
    fallbackErrMsg: string,
    solver: () => Promise<unknown>
  ): Promise<RndcResolverResult> {
    try {
      await solver();
    } catch (e) {
      console.error(e);
      const errorMessage = this.utils.getNestedValue(e, 'error.errorRNDC.error') as string;
      return { outcome: 'show-error-dialog', message: `${fallbackErrMsg} - ${errorMessage}` };
    }
    return this.rebuild(cargo);
  }

  private async rebuild(cargo: Cargo): Promise<RndcResolverResult> {
    try {
      await this.cargoTrackingService.rebuildManifest(cargo.id).toPromise();
      const fresh = await this.cargoTrackingService.detailCargoByConsecutive(cargo.consecutive.toString()).toPromise() as Cargo;
      if (!fresh) return { outcome: 'rebuild-failed-snackbar', consecutive: cargo.consecutive };
      if (fresh.manifestError && fresh.manifestError.error)
        return { outcome: 'show-error-dialog', message: fresh.manifestError.error };
      return { outcome: 'done-refresh', consecutive: cargo.consecutive };
    } catch (e) {
      console.error(e);
      return { outcome: 'rebuild-failed-snackbar', consecutive: cargo.consecutive };
    }
  }

  public hasUserInteractionError(cargo: Cargo): boolean {
    const manifestError = this.utils.getNestedValue(cargo, 'manifestError.error') as string;
    if (!manifestError) return false;
    const errorCode = this.rndcErrorSolver.getErrorCode(manifestError).toUpperCase();
    return this.errorsWithUserInteraction.includes(errorCode);
  }
}
